import { useEffect, useState, useCallback, useRef } from "react";
import { socket } from "./socket.service";
import { useAppSelector } from "../../../services/hooks";
import { CreateMessageDto, MessageDto } from "./socket-type";

// Типы для состояний
interface TypedUser {
  senderId: number;
  isTyping: boolean;
}

interface Conversation {
  conversationId: number;
  lastMessage?: string;
  lastMessageTime?: string;
  // Можно добавить другие необходимые поля
}

interface ConnectionState {
  isConnected: boolean;
  error: Error | null;
  reconnectAttempt: number;
}

// Интерфейсы для событий блокировки/разблокировки
interface BlockerEvent {
  blockedId: number; // ID заблокированного пользователя (приходит инициатору блокировки)
}

interface BlockedEvent {
  blockerId: number; // ID пользователя, который заблокировал (приходит заблокированному)
}

interface UnblockerEvent {
  unblockedId: number; // ID разблокированного пользователя (приходит инициатору разблокировки)
}

interface UnblockedEvent {
  unblockerId: number; // ID пользователя, который разблокировал (приходит разблокированному)
}

// Тип для обратного вызова обработки блокировки
type BlockStatusCallback = (
  userId: number,
  isBlocker: boolean,
  isBlocked: boolean,
) => void;

export const useMessages = () => {
  const { user } = useAppSelector((state) => state.user);
  const [messages, setMessages] = useState<MessageDto[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<number[]>([]);
  const [typingUsers, setTypingUsers] = useState<TypedUser>({
    senderId: -1,
    isTyping: false,
  });
  const [activeConversation, setActiveConversation] = useState<number | null>(
    null,
  );
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [connectionState, setConnectionState] = useState<ConnectionState>({
    isConnected: false,
    error: null,
    reconnectAttempt: 0,
  });
  const [socketError, setSocketError] = useState<{ message: string } | null>(
    null,
  );
  const lastErrorRef = useRef<{ message: string; timestamp: number } | null>(
    null,
  );

  // Колбэк для обработки событий блокировки/разблокировки
  const onBlockStatusChange = useRef<BlockStatusCallback | null>(null);

  // Для предотвращения утечек памяти при асинхронных операциях
  const isMounted = useRef(true);

  // Обработчик успешного подключения
  const handleConnect = useCallback(() => {
    setConnectionState((prev) => ({
      ...prev,
      isConnected: true,
      error: null,
      reconnectAttempt: 0,
    }));
  }, []);

  // Обработчик ошибок, полученных от сервера
  // Обработчик ошибок, полученных от сервера с фильтрацией дубликатов и автосбросом
  const handleSocketError = useCallback((error: { message: string }) => {
    if (!isMounted.current) return;

    const now = Date.now();
    const errorMsg = error.message;

    // Проверяем, является ли ошибка дубликатом предыдущей
    if (lastErrorRef.current) {
      const { message: lastMessage, timestamp: lastTimestamp } =
        lastErrorRef.current;

      // Если текст совпадает и прошло менее 5 секунд, считаем дубликатом
      const isDuplicate =
        lastMessage === errorMsg && now - lastTimestamp < 3000;

      if (isDuplicate) {
        console.log("[WebSocket] Отфильтрован дубликат ошибки:", errorMsg);
        return;
      }
    }

    // Обновляем информацию о последней ошибке
    lastErrorRef.current = {
      message: errorMsg,
      timestamp: now,
    };

    // Устанавливаем ошибку в состояние
    setSocketError(error);

    // Автоматически очищаем ошибку через 3 секунды
    // const timer = setTimeout(() => {
    //   if (isMounted.current) {
    //     setSocketError(null);
    //   }
    // }, 5000);

    // // Очищаем таймер при получении новой ошибки
    // return () => clearTimeout(timer);
  }, []);
  // Очистка ошибки
  const clearSocketError = useCallback(() => {
    setSocketError(null);
  }, []);

  // Обработчик отключения
  const handleDisconnect = useCallback(() => {
    setConnectionState((prev) => ({
      ...prev,
      isConnected: false,
    }));
  }, []);

  // Обработчик ошибок сокета
  const handleError = useCallback((error: Error) => {
    setConnectionState((prev) => ({
      ...prev,
      error,
      reconnectAttempt: prev.reconnectAttempt + 1,
    }));
  }, []);

  // Подключение сокета при монтировании и очистка при размонтировании
  // Оптимизируем подключение WebSocket в useMessages.ts
  useEffect(() => {
    isMounted.current = true;
    if (user && socket.disconnected) {
      // Подключаемся только если не подключены
      socket.connect();
      socket.on("connect", () => {
        handleConnect();
        socket.emit("joinRoom", `user_${user.id}`);
      });
      socket.on("disconnect", handleDisconnect);
      socket.on("connect_error", handleError);
    }

    return () => {
      isMounted.current = false;
      // Только отписываемся от событий, не отключаем сокет!
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleError);
      // Не вызываем socket.disconnect() здесь!
    };
  }, [user, handleConnect, handleDisconnect, handleError]);

  // Обработчик нового сообщения
  const handleNewMessage = useCallback((data: { message: MessageDto }) => {
    const { message } = data;
    setMessages((prevMessages) => {
      // Проверка на дубликаты сообщений
      const messageExists = prevMessages.some((msg) => msg.id === message.id);
      if (messageExists) return prevMessages;
      return [...prevMessages, message];
    });

    setConversations((prev) =>
      prev.map((conv) =>
        conv.conversationId === message.conversationId
          ? {
              ...conv,
              lastMessage: message.text,
              lastMessageTime: message.createdAt,
            }
          : conv,
      ),
    );
  }, []);

  // Обработчик набора текста
  const handleTyping = useCallback((data: TypedUser) => {
    setTypingUsers(data);
  }, []);

  // Обработчик прочтения сообщений
  const handleMessagesRead = useCallback(
    (data: { conversationId: number; readerId: number }) => {
      if (data.readerId !== user?.id) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.conversationId === data.conversationId && !msg.readAt
              ? {
                  ...msg,
                  isRead: true,
                  readAt: new Date().toISOString(),
                  status: "read",
                }
              : msg,
          ),
        );
      }
    },
    [user?.id],
  );

  // Обработчик статуса пользователя
  const handleUserStatusChanged = useCallback(
    (data: { userId: number; isOnline: boolean }) => {
      setOnlineUsers((prev) =>
        data.isOnline
          ? prev.includes(data.userId)
            ? prev
            : [...prev, data.userId]
          : prev.filter((id) => id !== data.userId),
      );
    },
    [],
  );

  // Обработчик блокировки для инициатора блокировки
  const handleUserBlockedAsBlocker = useCallback(
    (data: BlockerEvent) => {
      if (!user) return;

      // Вызываем внешний обработчик, если он предоставлен
      if (onBlockStatusChange.current) {
        onBlockStatusChange.current(data.blockedId, true, true);
      }
    },
    [user],
  );

  // Обработчик блокировки для заблокированного пользователя
  const handleUserBlockedAsBlocked = useCallback(
    (data: BlockedEvent) => {
      if (!user) return;

      // Вызываем внешний обработчик, если он предоставлен
      if (onBlockStatusChange.current) {
        onBlockStatusChange.current(data.blockerId, false, true);
      }
    },
    [user],
  );

  // Обработчик разблокировки для инициатора разблокировки
  const handleUserUnblockedAsUnblocker = useCallback(
    (data: UnblockerEvent) => {
      if (!user) return;

      // Вызываем внешний обработчик, если он предоставлен
      if (onBlockStatusChange.current) {
        onBlockStatusChange.current(data.unblockedId, true, false);
      }
    },
    [user],
  );

  // Обработчик разблокировки для разблокированного пользователя
  const handleUserUnblockedAsUnblocked = useCallback(
    (data: UnblockedEvent) => {
      if (!user) return;

      // Вызываем внешний обработчик, если он предоставлен
      if (onBlockStatusChange.current) {
        onBlockStatusChange.current(data.unblockerId, false, false);
      }
    },
    [user],
  );

  // Подписка на события блокировки/разблокировки
  useEffect(() => {
    // Обработка события userBlocked
    socket.on("userBlocked", (data: any) => {
      // Определяем тип события на основе полей в data
      if ("blockedId" in data) {
        handleUserBlockedAsBlocker(data);
      } else if ("blockerId" in data) {
        handleUserBlockedAsBlocked(data);
      }
    });

    // Обработка события userUnblocked
    socket.on("userUnblocked", (data: any) => {
      if ("unblockedId" in data) {
        handleUserUnblockedAsUnblocker(data);
      } else if ("unblockerId" in data) {
        handleUserUnblockedAsUnblocked(data);
      }
    });

    return () => {
      socket.off("userBlocked");
      socket.off("userUnblocked");
    };
  }, [
    handleUserBlockedAsBlocker,
    handleUserBlockedAsBlocked,
    handleUserUnblockedAsUnblocker,
    handleUserUnblockedAsUnblocked,
  ]);

  // Подписка на основные события сокета
  useEffect(() => {
    socket.on("newMessage", handleNewMessage);
    socket.on("error", handleSocketError);

    socket.on("typing", handleTyping);
    socket.on("messagesRead", handleMessagesRead);
    socket.on("userStatusChanged", handleUserStatusChanged);

    return () => {
      socket.off("newMessage", handleNewMessage);
      socket.off("typing", handleTyping);
      socket.off("messagesRead", handleMessagesRead);
      socket.off("userStatusChanged", handleUserStatusChanged);
      socket.off("error", handleSocketError);
    };
  }, [
    handleNewMessage,
    handleTyping,
    handleMessagesRead,
    handleUserStatusChanged,
    handleSocketError,
  ]);

  // Установка обработчика событий блокировки
  const setBlockStatusChangeHandler = useCallback(
    (handler: BlockStatusCallback) => {
      onBlockStatusChange.current = handler;
    },
    [],
  );

  const setTyping = useCallback((recipientId: number, isTyping: boolean) => {
    socket.emit("typing", { recipientId, isTyping });
  }, []);

  // Методы для работы с чатом
  const sendMessage = useCallback(
    (createMessageDto: CreateMessageDto) => {
      setTyping(createMessageDto.recipientId, false);
      socket.emit("sendMessage", createMessageDto);
    },
    [setTyping],
  );

  // Отправка события блокировки пользователя
  const blockUser = useCallback((blockedId: number) => {
    if (!socket || !blockedId) return;
    socket.emit("blockUser", { blockedId });
  }, []);

  // Отправка события разблокировки пользователя
  const unblockUser = useCallback((unblockedId: number) => {
    if (!socket || !unblockedId) return;
    socket.emit("unblockUser", { unblockedId });
  }, []);

  const markAsRead = useCallback((conversationId: number) => {
    socket.emit("markAsRead", { conversationId });
  }, []);

  const joinConversation = useCallback(
    (conversationId: number) => {
      if (activeConversation) {
        socket.emit("leaveConversation", activeConversation);
      }
      socket.emit("joinConversation", conversationId);
      setActiveConversation(conversationId);
      markAsRead(conversationId);
    },
    [activeConversation, markAsRead],
  );

  const leaveConversation = useCallback(() => {
    if (activeConversation) {
      socket.emit("leaveConversation", activeConversation);
      setActiveConversation(null);
    }
  }, [activeConversation]);

  // Установка сообщений из истории
  const setHistoryMessages = useCallback((historyMessages: MessageDto[]) => {
    setMessages(historyMessages);
  }, []);

  // Очистка сообщений
  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  // Метод для добавления или обновления диалогов
  const updateConversations = useCallback(
    (newConversations: Conversation[]) => {
      setConversations(newConversations);
    },
    [],
  );

  // Возможность ручного переподключения
  const reconnect = useCallback(() => {
    if (!connectionState.isConnected) {
      socket.connect();
    }
  }, [connectionState.isConnected]);

  // Автоматическая попытка переподключения при ошибке
  useEffect(() => {
    if (
      connectionState.error &&
      connectionState.reconnectAttempt < 5 &&
      !connectionState.isConnected
    ) {
      const timeout = setTimeout(() => {
        reconnect();
      }, 2000 * connectionState.reconnectAttempt);
      return () => clearTimeout(timeout);
    }
  }, [connectionState, reconnect]);

  return {
    messages,
    onlineUsers,
    typingUsers,
    activeConversation,
    setActiveConversation,
    conversations,
    connectionState,
    sendMessage,
    setTyping,
    markAsRead,
    joinConversation,
    leaveConversation,
    setHistoryMessages,
    clearMessages,
    updateConversations,
    reconnect,
    blockUser,
    unblockUser,
    setBlockStatusChangeHandler,
    socketError,
    clearSocketError,
  };
};
